import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileText, Filter, RefreshCw } from "lucide-react";
import DatePicker from "@/components/site/DatePicker";
import { toast } from "sonner";

// Column order requested by user (latest revision):
//   TXN No. → Issuance Date → Airline → Flight No. →
//   Route → Pax → Flight Date →
//   API Amount → API Currency → FX Rate → FX Margin → FX Effective Rate →
//   Base Amount → Tax Amount → Airline Commission % →
//   Total Before Discount → Promo Code → Promo Discount → Markup Amount →
//   Total After Discount → PNR → Ticket Number → Airline Tour Code →
//   Supplier
const DEFAULT_FIELDS = [
  "txn_no", "issuance_date", "airline", "supplier", "flight_no",
  "route", "pax_count", "flight_date",
  "api_amount", "api_currency", "fx_rate", "fx_margin", "fx_effective_rate",
  "base_amount", "tax_amount", "airline_commission_pct",
  "total_before_discount",
  "promo_code", "promo_discount", "markup_amount",
  "total_after_discount",
  "pnr", "ticket_number", "airline_tour_code",
];
const BOOKING_TYPES = ["flight", "hotel", "car", "package", "sightseeing"];

export default function AdminCustomReport() {
  const [allFields, setAllFields] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_FIELDS);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bookingType, setBookingType] = useState("flight");
  const [status, setStatus] = useState("paid");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/fields");
        setAllFields(data);
      } catch (_e) { /* */ }
    })();
  }, []);

  const toggleField = (k, on) => {
    setSelected((prev) => on ? [...new Set([...prev, k])] : prev.filter((x) => x !== k));
  };
  const selectAll = () => setSelected(allFields.map((f) => f.key));
  const selectDefault = () => setSelected(DEFAULT_FIELDS);

  const run = async () => {
    if (selected.length === 0) return toast.error("Select at least one column");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/custom", {
        from_date: fromDate || null,
        to_date: toDate || null,
        booking_type: bookingType,
        status,
        fields: selected,
      });
      setData(data);
    } catch (_e) { toast.error("Failed to load report"); }
    finally { setBusy(false); }
  };

  const download = async (fmt) => {
    if (selected.length === 0) return toast.error("Select at least one column");
    const token = localStorage.getItem("fa_admin_token");
    const res = await fetch(`${BACKEND_URL}/api/admin/reports/custom/download?fmt=${fmt}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        from_date: fromDate || null, to_date: toDate || null,
        booking_type: bookingType, status, fields: selected,
      }),
    });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `report_${new Date().toISOString().slice(0, 10)}.${fmt}`;
    a.click();
    URL.revokeObjectURL(u);
    toast.success(`Downloaded ${fmt.toUpperCase()}`);
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Custom report</h1>
      <p className="text-slate-500 mt-1">Build a tailored sales report with the fields and date range you need. Preview, then download as CSV or Excel.</p>

      {/* Filters */}
      <Card className="mt-6 p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <DatePicker testId="cr-from-date" label="From" value={fromDate} onChange={setFromDate} placeholder="Any" />
          </div>
          <div className="md:col-span-3">
            <DatePicker testId="cr-to-date" label="To" value={toDate} onChange={setToDate} minDate={fromDate} placeholder="Any" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">Product</Label>
            <Select value={bookingType} onValueChange={setBookingType}>
              <SelectTrigger data-testid="cr-booking-type" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{BOOKING_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="cr-status" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Paid only</SelectItem>
                <SelectItem value="all">All bookings</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button data-testid="cr-run-btn" onClick={run} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full flex-1">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Filter className="w-4 h-4 mr-1" /> Run</>}
            </Button>
          </div>
        </div>
      </Card>

      {/* Field selector */}
      <Card className="mt-5 p-5" data-testid="cr-fields-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Columns to include</div>
            <div className="text-xs text-slate-500">Toggle the fields you want in the preview & download.</div>
          </div>
          <div className="flex gap-2 text-xs">
            <button onClick={selectAll} className="text-[var(--brand-primary)] hover:underline">Select all</button>
            <span className="text-slate-300">·</span>
            <button onClick={selectDefault} className="text-slate-500 hover:underline">Reset defaults</button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {allFields.map((f) => (
            <label key={f.key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer transition-colors ${selected.includes(f.key) ? "border-teal-300 bg-teal-50/60 text-[var(--brand-accent)]" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Checkbox
                data-testid={`cr-field-${f.key}`}
                checked={selected.includes(f.key)}
                onCheckedChange={(c) => toggleField(f.key, !!c)}
              />
              <span className="font-medium">{f.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-slate-400">
          {selected.length} of {allFields.length} columns selected
        </div>
      </Card>

      {/* Preview + totals + download */}
      {data && (
        <>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Rows</div><div data-testid="cr-totals-rows" className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">{data.totals.rows}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Base ($)</div><div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">{Number(data.totals.base_amount).toLocaleString()}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Markup ($)</div><div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">{Number(data.totals.markup_amount).toLocaleString()}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total ($)</div><div data-testid="cr-totals-amount" className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">{Number(data.totals.total_amount).toLocaleString()}</div></Card>
          </div>

          <Card className="mt-5 overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Preview</div>
              <div className="flex gap-2">
                <Button data-testid="cr-download-csv" variant="outline" size="sm" onClick={() => download("csv")}><FileText className="w-3.5 h-3.5 mr-1" /> CSV</Button>
                <Button data-testid="cr-download-xlsx" onClick={() => download("xlsx")} size="sm" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]"><Download className="w-3.5 h-3.5 mr-1" /> Excel</Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {data.fields.map((f) => <TableHead key={f}>{data.labels[f]}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={data.fields.length} className="text-center text-slate-400 py-10">No bookings in this range</TableCell></TableRow>
                ) : (
                  data.rows.map((r, i) => (
                    <TableRow key={i} data-testid={`cr-row-${i}`}>
                      {data.fields.map((f) => {
                        const val = r[f];
                        const isMoneyUsd = ["base_amount","tax_amount","markup_amount","total_amount","total_before_discount","total_after_discount","promo_discount"].includes(f);
                        const isApiAmount = f === "api_amount";
                        const isFxRate = f === "fx_rate";
                        const isFxEff = f === "fx_effective_rate";
                        const isFxMargin = f === "fx_margin";
                        const isCommission = f === "airline_commission_pct";
                        const isTourCode = f === "airline_tour_code";
                        const isPromoCode = f === "promo_code";
                        const isPnr = f === "pnr";
                        const isTicket = f === "ticket_number";
                        const isTxn = f === "txn_no";
                        let display = val ?? "—";
                        if (isMoneyUsd) display = `$${Number(val || 0).toFixed(2)}`;
                        else if (isApiAmount && typeof val === "number") {
                          const ccy = r.api_currency && r.api_currency !== "—" ? r.api_currency : "";
                          display = `${ccy ? ccy + " " : ""}${Number(val).toFixed(2)}`;
                        }
                        else if ((isFxRate || isFxEff) && typeof val === "number") display = Number(val).toFixed(6);
                        else if (isFxMargin && typeof val === "number") display = `${Number(val).toFixed(2)}%`;
                        else if (isCommission && typeof val === "number") display = `${Number(val).toFixed(2)}%`;
                        const hasMargin = isFxEff && typeof r.fx_margin === "number" && r.fx_margin > 0;
                        const cls = isMoneyUsd || isApiAmount || isFxRate
                          ? "font-mono"
                          : isFxEff ? `font-mono ${hasMargin ? "text-emerald-700 font-semibold" : ""}`
                          : isTxn ? "font-mono font-bold text-[var(--brand-primary-dark)]"
                          : isPnr && val && val !== "—" ? "font-mono font-bold text-[var(--brand-accent)]"
                          : isTicket && val && val !== "—" ? "font-mono text-xs whitespace-nowrap"
                          : isPromoCode && val && val !== "—" ? "font-mono uppercase text-[var(--brand-primary-dark)]"
                          : isCommission && val && val !== "—" ? "font-mono font-semibold text-emerald-700"
                          : isTourCode && val && val !== "—" ? "font-mono font-semibold text-[var(--brand-primary-dark)]"
                          : "";
                        return (
                          <TableCell key={f} className={cls}>{display}</TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
